import { useEffect, useRef, useState } from "react";

import { PaymentMethod } from "features/payment-methods/components";
import useSubscriptionPaymentSource from "features/payment-methods/datahooks/useSubscriptionPaymentSource";
import type { PaymentSource } from "features/payment-methods/types";
import { CancellationFlow } from "features/subscriptions/components";
import AccountsToCancel from "features/subscriptions/components/ManageSubscription/AccountsToCancel";
import ManageSubscriptionDetails from "features/subscriptions/components/ManageSubscription/ManageSubscriptionDetails";
import SubscriptionActionButton from "features/subscriptions/components/ManageSubscription/SubscriptionActionButton";
import SubscriptionPaymentSource from "features/subscriptions/components/ManageSubscription/SubscriptionPaymentSource";
import SuccessLarge from "features/subscriptions/components/ManageSubscription/SuccessLarge";
import { useManageSubscription } from "features/subscriptions/context/ManageSubscriptionContext";
import { getRequiredAccountIdsCountForDowngrade } from "features/subscriptions/helpers/rentalSubscriptions";
import useCreateSubscription from "features/subscriptions/hooks/useCreateSubscription";
import useManageSubscriptionEstimate from "features/subscriptions/hooks/useManageSubscriptionEstimate";
import type useManageSubscriptionSteps from "features/subscriptions/hooks/useManageSubscriptionSteps";
import useMinSeatQuantity from "features/subscriptions/hooks/useMinSeatQuantity";
import useUpdateSubscription from "features/subscriptions/hooks/useUpdateSubscription";
import type { MixpanelSubscriptionSource } from "features/subscriptions/types";
import { parseEstimate } from "features/subscriptions/utils";

import ErrorMessage from "common/components/ui/ErrorMessage";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePromotions from "common/datahooks/usePromotions";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import { trackMixpanel } from "common/helpers/utils";
import useExternalCouponState from "common/hooks/useExternalCouponState";

import Success from "./Success";

type UseManageSubscriptionStepsType = ReturnType<
  typeof useManageSubscriptionSteps
>;

interface ManageSubscriptionProps {
  step: UseManageSubscriptionStepsType["manageSubscriptionStep"];
  goToStep: UseManageSubscriptionStepsType["goToManageSubscriptionStep"];
  mixpanelSource: MixpanelSubscriptionSource;
}

export default function ManageSubscription({
  step,
  goToStep,
  mixpanelSource,
}: ManageSubscriptionProps) {
  const { id: workspaceId, accounts } = useSelectedWorkspaceContext();
  const {
    initialState,
    seatQuantity,
    pricing,
    subscriptionType,
    hasActiveSubscription,
    hasExistingSubscription,
    isUnchanged,
    isDowngrade,
    isPricingPeriodDowngrade,
    isCancellation,
    rentalOrderSubscription,
  } = useManageSubscription();
  //this is used by mobile to go to summary step directly
  const isPaymentPreviousStepRef = useRef<boolean>(false);

  const { minSeatQuantity, isAgencyPurchase } = useMinSeatQuantity(
    subscriptionType,
    hasActiveSubscription,
    pricing?.plan,
  );

  const { subscriptionPaymentSource, isLoadingSubscriptionPaymentSource } =
    useSubscriptionPaymentSource(
      hasExistingSubscription && initialState.subscription.id,
      subscriptionType === "agency",
    );
  const { agencyPlan } = usePromotions();
  const { workspace } = useWorkspaceDetails(workspaceId);
  const { couponState, clearCoupon } = useExternalCouponState();

  //this is either "remove" or a new coupon id
  const [couponAction, setCouponAction] = useState<string | "remove">(
    couponState ? couponState.couponCode : undefined,
  );
  const [paymentSource, setPaymentSource] = useState<PaymentSource>();
  const [accountsToCancel, setAccountsToCancel] = useState([]);

  const {
    createSubscription,
    isCreatingSubscription,
    createSubscriptionError,
  } = useCreateSubscription(
    subscriptionType,
    couponAction && couponAction !== "remove" && couponAction,
  );
  const {
    updateSubscription,
    isUpdatingSubscription,
    updateSubscriptionError,
  } = useUpdateSubscription(
    subscriptionType,
    hasExistingSubscription ? initialState.subscription : undefined,
  );

  const {
    updateOrCreateEstimate,
    toPayNowEstimate,
    isLoadingEstimate,
    estimateError,
  } = useManageSubscriptionEstimate({ couponAction });

  //set payment source to the subscription payment source OR the primary one
  useEffect(() => {
    if (!paymentSource) {
      if (subscriptionPaymentSource) {
        setPaymentSource(subscriptionPaymentSource);
      }
    }
  }, [subscriptionPaymentSource, paymentSource]);

  //changing the seat count invalidates any previously selected accounts
  useEffect(() => {
    setAccountsToCancel([]);
  }, [seatQuantity]);

  let assignedLicenseCount = 0;

  if (subscriptionType === "agency") {
    if (agencyPlan) {
      const { license_count, unassigned_license_count } = agencyPlan;
      assignedLicenseCount = license_count - unassigned_license_count;
    }
  } else if (workspace) {
    assignedLicenseCount = workspace.licenses.filter(
      ({ account_id, source }) => !!account_id && source === "aimfox",
    ).length;
  }

  let seatsToCancel = 0;

  if (rentalOrderSubscription) {
    seatsToCancel = getRequiredAccountIdsCountForDowngrade(
      rentalOrderSubscription,
      seatQuantity,
      accounts,
    );
  } else if (subscriptionType === "user") {
    seatsToCancel = assignedLicenseCount - seatQuantity;
  }

  let couponUnapplicableError: Error;

  if (couponAction && couponAction !== "remove" && updateOrCreateEstimate) {
    if (!parseEstimate(updateOrCreateEstimate).coupon) {
      couponUnapplicableError = new Error(
        `Coupon ${couponAction.toUpperCase()} unapplicable`,
      );
    }
  }

  const isCouponChangeOnly =
    isUnchanged && hasActiveSubscription && !!couponAction;

  const isButtonDisabled =
    (hasActiveSubscription &&
      !isCouponChangeOnly &&
      (isUnchanged ||
        (subscriptionType === "agency" && isPricingPeriodDowngrade) ||
        (subscriptionType === "agency" &&
          seatQuantity < assignedLicenseCount))) ||
    // minSeatQuantity loads async and can rise after seatQuantity is
    // set, so this guards the brief window before seatQuantity catches up
    (isAgencyPurchase && seatQuantity < minSeatQuantity) ||
    isLoadingEstimate ||
    !!estimateError ||
    !!couponUnapplicableError;

  async function onButtonClick() {
    //rental orders downgrade to 0 through the regular update path instead
    if (isCancellation && !rentalOrderSubscription) {
      goToStep("cancel");
      return;
    }
    if (!paymentSource) {
      goToStep("paymentMethod");
      return;
    }
    //the "manage" check prevents re-routing when submitting from AccountsToCancel
    if (step === "manage" && seatsToCancel > 0) {
      goToStep("unassignLicenses");
      return;
    }
    const data = {
      quantity: seatQuantity,
      paymentSourceId: paymentSource.id,
      planId: pricing.plan.id,
    };
    if (hasExistingSubscription) {
      const { invoice_estimate } = toPayNowEstimate;
      const paidAmount = invoice_estimate
        ? parseEstimate(invoice_estimate).amountDue
        : 0;
      await updateSubscription({
        ...data,
        accountsToCancel,
        paidAmount,
        couponAction,
      });
    } else {
      const { amountDue } = parseEstimate(updateOrCreateEstimate);
      await createSubscription({ ...data, planValue: amountDue });
    }

    if (!isDowngrade) {
      trackMixpanel("upgrade", mixpanelSource);
    }

    clearCoupon();
    goToStep("success");
  }

  const subscriptionActionButton = (
    <SubscriptionActionButton
      isLoading={isCreatingSubscription || isUpdatingSubscription}
      isDisabled={isButtonDisabled}
      onClick={onButtonClick}
    />
  );

  const errorMessageComponent = (
    <ErrorMessage
      error={
        estimateError ||
        createSubscriptionError ||
        updateSubscriptionError ||
        couponUnapplicableError
      }
      fallbackErrorMessage={
        estimateError ? "Error getting estimate" : "Error mutating subscription"
      }
      size="sm"
    />
  );

  if (step === "manage") {
    const paymentSourceComponent = (
      <SubscriptionPaymentSource
        paymentSource={paymentSource}
        onEdit={() => goToStep("paymentMethod")}
        isLoading={isLoadingSubscriptionPaymentSource}
      />
    );
    return (
      <ManageSubscriptionDetails
        couponAction={couponAction}
        setCouponAction={setCouponAction}
        paymentSourceComponent={paymentSourceComponent}
        isPaymentPreviousStep={isPaymentPreviousStepRef.current}
        subscriptionActionButton={subscriptionActionButton}
        errorMessageComponent={errorMessageComponent}
      />
    );
  } else if (step === "paymentMethod") {
    return (
      <PaymentMethod
        initialPaymentSource={paymentSource}
        onBack={() => {
          isPaymentPreviousStepRef.current = true;
          goToStep("manage");
        }}
        setPaymentSource={(newPaymentSource) => {
          isPaymentPreviousStepRef.current = true;
          setPaymentSource(newPaymentSource);
          goToStep("manage");
        }}
        mixpanelSource={mixpanelSource}
      />
    );
  } else if (step === "success") {
    return <Success />;
  } else if (step === "unassignLicenses") {
    return (
      <AccountsToCancel
        accountsToCancel={accountsToCancel}
        setAccountsToCancel={setAccountsToCancel}
        seatsToCancel={seatsToCancel}
        goBack={() => goToStep("manage")}
        updateSubscriptionButton={subscriptionActionButton}
      />
    );
  } else if (step === "cancel" && hasExistingSubscription) {
    return (
      <CancellationFlow
        subscriptionId={initialState.subscription.id}
        mode="skipConfirmation"
        onCancellationSuccess={() => {
          goToStep("success");
        }}
        goBack={() => {
          goToStep("manage");
        }}
      />
    );
  } else {
    return <SuccessLarge subscriptionType={subscriptionType} />;
  }
}
